import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { History as HistoryIcon, PhoneOutgoing, RotateCcw, Star } from "lucide-react";

function statusBadge(status) {
  const map = {
    completed: "bg-accent text-accent-foreground",
    "in-progress": "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
  };
  return map[status] || "bg-zinc-100 text-zinc-600";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(s) {
  if (!s) return "0:00";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function History() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/calls").then(({ data }) => {
      setCalls(data);
      setLoading(false);
    });
  }, []);

  const redial = (c) => {
    navigate("/app", {
      state: { redial: { number: c.number, dial_code: c.dial_code, country: c.country } },
    });
  };

  const renderBody = () => {
    if (loading) return <div className="p-8 text-center text-zinc-400">Loading…</div>;
    if (calls.length === 0)
      return (
        <div className="bg-white border border-black/10 rounded-2xl p-12 text-center shadow-sm" data-testid="history-empty">
          <PhoneOutgoing className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500 font-medium">No calls yet</p>
          <p className="text-zinc-400 text-sm">Your call receipts will show up here.</p>
        </div>
      );
    return (
      <div className="space-y-3" data-testid="history-list">
        {calls.map((c) => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            onClick={() => redial(c)}
            onKeyDown={(e) => { if (e.key === "Enter") redial(c); }}
            title="Tap to redial"
            className="bg-white border border-black/10 rounded-2xl p-4 shadow-sm flex items-center gap-4 cursor-pointer hover:border-primary hover:shadow-md transition-all active:scale-[0.99]"
            data-testid={`call-row-${c.id}`}
          >
            <div className="text-3xl">{c.flag}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.number}</div>
              <div className="text-sm text-zinc-500">{c.country} • {fmtDate(c.started_at)}</div>
              {c.rating >= 1 && (
                <div className="flex items-center gap-0.5 mt-0.5" data-testid={`call-rating-${c.id}`}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className={`h-3 w-3 ${s <= c.rating ? "text-amber-400 fill-amber-400" : "text-zinc-200"}`} />
                  ))}
                </div>
              )}
              {c.status === "failed" && c.fail_reason && (
                <div className="text-xs text-red-600 mt-0.5" data-testid={`call-fail-reason-${c.id}`}>{c.fail_reason}</div>
              )}
            </div>
            <div className="text-right">
              <div className="font-mono font-bold" data-testid={`call-duration-${c.id}`}>{fmtDuration(c.duration_seconds)}</div>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize hidden sm:inline ${statusBadge(c.status)}`}>
              {c.status}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); redial(c); }}
              data-testid={`redial-${c.id}`}
              title="Redial this number"
              className="h-10 w-10 shrink-0 rounded-xl bg-accent text-accent-foreground flex items-center justify-center hover:bg-emerald-100 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
          <HistoryIcon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Call history</h1>
          <p className="text-zinc-500 text-sm">Your recent calls and how long they lasted.</p>
        </div>
      </div>

      {renderBody()}
    </div>
  );
}
